import apiClient from './apiClient';
import type {
  WalletBalance,
  WalletTransaction,
  TransactionListResponse,
  KhaltiPaymentResponse,
  KhaltiVerificationRequest,
  KhaltiVerificationResponse,
} from './types';
import { formatCurrency as formatWireCurrency, toNumber } from './numberUtils';

type TransactionHistoryFilters = {
  transactionType?: WalletTransaction['type'];
  fromDate?: string;
  toDate?: string;
};

const WALLET_TOPUP_LIMITS = {
  minAmount: 10,
  maxAmount: 50000,
  suggestedAmounts: [100, 250, 500, 1000, 2000, 5000],
};

const normalizeTransaction = (transaction: WalletTransaction): WalletTransaction => ({
  ...transaction,
  amount_npr: toNumber(transaction.amount_npr),
  balance_after: toNumber(transaction.balance_after),
});

const normalizeWalletBalance = (balance: WalletBalance): WalletBalance => ({
  ...balance,
  current_balance: toNumber(balance.current_balance),
  recent_transactions: (balance.recent_transactions ?? []).map(normalizeTransaction),
});

class WalletService {
  async getWalletBalance(): Promise<WalletBalance> {
    try {
      const response = await apiClient.get<WalletBalance>('/api/v1/wallets/balance');
      return normalizeWalletBalance(response.data);
    } catch (error: any) {
      if (error.response?.data?.detail) {
        throw new Error(error.response.data.detail);
      }

      throw new Error(error.message || 'Failed to get wallet balance');
    }
  }

  async getTransactionHistory(
    cursor: string | null = null,
    perPage: number = 20,
    filters: TransactionHistoryFilters = {}
  ): Promise<{ items: WalletTransaction[]; nextCursor: string | null }> {
    const params = new URLSearchParams({
      limit: String(perPage),
    });
    if (cursor) params.set('cursor', cursor);

    if (filters.transactionType) {
      params.set('transaction_type', filters.transactionType);
    }

    if (filters.fromDate) {
      params.set('from_date', filters.fromDate);
    }

    if (filters.toDate) {
      params.set('to_date', filters.toDate);
    }

    const response = await apiClient.get<TransactionListResponse>(
      `/api/v1/wallets/transactions?${params.toString()}`
    );

    return {
      items: response.data.transactions.map(normalizeTransaction),
      nextCursor: response.data.next_cursor ?? null,
    };
  }

  async getRecentTransactions(limit: number = 5): Promise<WalletTransaction[]> {
    const response = await this.getTransactionHistory(null, limit);
    return response.items;
  }

  async initiateKhaltiTopup(amount: number): Promise<KhaltiPaymentResponse> {
    const response = await apiClient.post<KhaltiPaymentResponse>(
      '/api/v1/payments/khalti/initiate',
      { amount }
    );

    return {
      ...response.data,
      amount: toNumber(response.data.amount),
    };
  }

  async verifyKhaltiPayment(
    request: KhaltiVerificationRequest
  ): Promise<KhaltiVerificationResponse> {
    const response = await apiClient.post<KhaltiVerificationResponse>(
      '/api/v1/payments/khalti/verify',
      request
    );

    return {
      ...response.data,
      new_balance: response.data.new_balance == null
        ? response.data.new_balance
        : toNumber(response.data.new_balance),
      transaction: response.data.transaction
        ? normalizeTransaction(response.data.transaction)
        : response.data.transaction,
    };
  }

  async checkSufficientBalance(requiredAmount: number): Promise<{
    sufficient: boolean;
    current_balance: number;
    amount_needed: number;
  }> {
    const wallet = await this.getWalletBalance();

    return {
      sufficient: wallet.current_balance >= requiredAmount,
      current_balance: wallet.current_balance,
      amount_needed: Math.max(0, requiredAmount - wallet.current_balance),
    };
  }

  getSuggestedTopupAmounts(): number[] {
    return WALLET_TOPUP_LIMITS.suggestedAmounts;
  }

  getTopupLimits(): { minAmount: number; maxAmount: number } {
    return {
      minAmount: WALLET_TOPUP_LIMITS.minAmount,
      maxAmount: WALLET_TOPUP_LIMITS.maxAmount,
    };
  }

  async getOptimalTopupAmount(
    packPrice: number,
    bufferPercentage: number = 0.2
  ): Promise<{
    minimum_topup: number;
    recommended_topup: number;
    suggested_amounts: number[];
  }> {
    const balance = await this.getWalletBalance();
    const shortfall = Math.max(0, packPrice - balance.current_balance);

    if (shortfall === 0) {
      return {
        minimum_topup: 0,
        recommended_topup: 0,
        suggested_amounts: [],
      };
    }

    const recommendedTopup = Math.ceil(shortfall * (1 + bufferPercentage));
    const suggestedAmounts = this.getSuggestedTopupAmounts()
      .filter(amount => amount >= shortfall)
      .slice(0, 4);

    return {
      minimum_topup: shortfall,
      recommended_topup: recommendedTopup,
      suggested_amounts: suggestedAmounts,
    };
  }

  async deductFromWallet(
    amount: number,
    description: string,
    referenceId?: string
  ): Promise<{
    success: boolean;
    new_balance: number;
    transaction_id: string;
  }> {
    const response = await apiClient.post<any>(
      '/api/v1/wallets/deduct',
      {
        amount,
        description,
        reference_id: referenceId,
      }
    );

    return {
      ...response.data,
      new_balance: toNumber(response.data.new_balance),
    };
  }

  async getWalletStats(): Promise<{
    total_topped_up: number;
    total_spent: number;
    average_monthly_spending: number;
    packs_purchased_count: number;
  }> {
    const response = await apiClient.get<any>('/api/v1/wallets/stats');
    return {
      total_topped_up: toNumber(response.data.total_topped_up),
      total_spent: toNumber(response.data.total_spent),
      average_monthly_spending: toNumber(response.data.average_monthly_spending),
      packs_purchased_count: toNumber(response.data.packs_purchased_count),
    };
  }

  formatCurrency(amount: number | string | undefined | null, currency: string = 'NPR'): string {
    return formatWireCurrency(amount, currency);
  }

  validateTopupAmount(amount: number): {
    valid: boolean;
    error?: string;
  } {
    if (amount < WALLET_TOPUP_LIMITS.minAmount) {
      return {
        valid: false,
        error: `Minimum top-up amount is ${this.formatCurrency(WALLET_TOPUP_LIMITS.minAmount)}`,
      };
    }

    if (amount > WALLET_TOPUP_LIMITS.maxAmount) {
      return {
        valid: false,
        error: `Maximum top-up amount is ${this.formatCurrency(WALLET_TOPUP_LIMITS.maxAmount)}`,
      };
    }

    if (amount % 1 !== 0) {
      return {
        valid: false,
        error: 'Amount must be a whole number',
      };
    }

    return { valid: true };
  }

  async handlePaymentCallback(
    pidx: string,
    transactionId?: string,
    status?: string
  ): Promise<{
    success: boolean;
    message: string;
    new_balance?: number;
  }> {
    try {
      const result = await this.verifyKhaltiPayment({
        pidx,
        transaction_id: transactionId,
        status,
      });

      return {
        success: result.success,
        message: result.message || this.getPaymentStatusMessage(result.status),
        new_balance: result.new_balance ?? undefined,
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.message || 'Payment verification failed',
      };
    }
  }

  private getPaymentStatusMessage(status?: string): string {
    switch (status) {
      case 'User canceled':
        return 'Payment was cancelled by user';
      case 'Expired':
        return 'Payment link has expired';
      case 'Pending':
        return 'Payment is still being processed';
      default:
        return status ? `Payment ${status.toLowerCase()}` : 'Payment status is unknown';
    }
  }
}

export const walletService = new WalletService();
export default walletService;
